import React from 'react';
import EnterpriseLanding from '../pages/EnterpriseLanding';
import TagcLanding from '../pages/TagcLanding';

/* ====================================================================
 * BrandRoot — single source of truth for what renders at "/".
 *
 * Decision precedence (most authoritative first):
 *
 *   1. HOSTNAME (defensive safety belt — production canonical):
 *        tagc.us / www.tagc.us         → TagcLanding
 *        toxintel.tagc.us              → EnterpriseLanding
 *
 *   2. REACT_APP_BRAND (build-time env, for preview/staging hosts
 *      where hostname check yields no match):
 *        "tagc"                        → TagcLanding
 *        "toxintel"                    → EnterpriseLanding
 *
 *   3. Safe default → EnterpriseLanding (ToxIntel is the
 *      organization's canonical product surface).
 *
 * Hostname check guarantees that:
 *   - tagc.us NEVER falls back to ToxIntel even if the deployment is
 *     misconfigured (REACT_APP_BRAND missing/wrong).
 *   - toxintel.tagc.us NEVER accidentally serves TAGC.
 *   - Desktop and mobile behave identically (no device-sniffing).
 *   - The result is deterministic on first render — no cache or
 *     session-state dependency.
 *
 * No URL-bar mutation. No redirect. Just renders the right component.
 * ==================================================================== */

const TAGC_HOSTS = new Set(['tagc.us', 'www.tagc.us']);
const TOXINTEL_HOSTS = new Set(['toxintel.tagc.us']);

function brandFromHost() {
  if (typeof window === 'undefined') return null;
  const host = (window.location.hostname || '').toLowerCase();
  if (TAGC_HOSTS.has(host)) return 'tagc';
  if (TOXINTEL_HOSTS.has(host)) return 'toxintel';
  return null;
}

function brandFromEnv() {
  const v = (process.env.REACT_APP_BRAND || '').toLowerCase();
  return v === 'tagc' || v === 'toxintel' ? v : null;
}

export default function BrandRoot() {
  const brand = brandFromHost() || brandFromEnv() || 'toxintel';
  if (brand === 'tagc') {
    return <TagcLanding />;
  }
  return <EnterpriseLanding />;
}
